import { checkCookie, setCookie } from '../utils/cookie-utils';
import { getDeviceInfo } from '../utils/device-utils';
import { ConfigService, CustomerInfo, MoengageConfig, ProgramConfig } from './config-service';

class MoengageService {
  private static instance: MoengageService;
  userType: 'unregistered' | 'Registered User' = 'unregistered';
  uniqueId = '';
  moengageConfig: MoengageConfig;
  customerInfo: CustomerInfo;
  programConfig: ProgramConfig;
  moengage: any; //import and assign moengage sdk only if moengage is enabled
  cookieExistsPayzapp = false;
  cookieExistsMycards = false;
  basicValues: {
    browser: string;
    device_type: string;
    program_name: string;
    user_type: string;
    booking_type: string;
    platform_source: string;
    sub_program: string;
  };

  static getInstance() {
    if (!MoengageService.instance) {
      MoengageService.instance = new MoengageService();
    }
    return MoengageService.instance;
  }

  constructor() {
    this.moengageConfig = ConfigService.moengage;
    this.customerInfo = ConfigService.customerInfo || {};
    this.programConfig = ConfigService.programConfig;
    if (this.moengageConfig?.enabled) {
      this.initializeMoengage();
    }
  }

  async initializeMoengage() {
    try {
      const Moengage = await import('@moengage/web-sdk');
      this.moengage = Moengage.default;
      let cookieExistsPayzap: boolean = checkCookie(this.moengageConfig.payzappCookiename);
      let cookieExistsMycards: boolean = checkCookie(this.moengageConfig.mycardsCookiename);
      if (!cookieExistsPayzap) {
        cookieExistsPayzap = this.checkLocal(this.moengageConfig.payzappCookiename);
        if (cookieExistsPayzap) setCookie(this.moengageConfig.payzappCookiename, 'PayZapp', { expires: 1 });
      }
      if (!cookieExistsMycards) {
        cookieExistsMycards = this.checkLocal(this.moengageConfig.mycardsCookiename);
        if (cookieExistsMycards) setCookie(this.moengageConfig.mycardsCookiename, 'MyCards', { expires: 1 });
      }
      this.commonDeviceDetails(cookieExistsPayzap, cookieExistsMycards);

      this.moengage.initialize({ app_id: this.moengageConfig.appId, cluster: 'DC_3', debug_logs: this.moengageConfig.debugMode });
      if (this.moengageConfig.debugMode) {
        this.moengage.setDebugLevel(2);
      }
    } catch (err: any) {
      console.error('MoEngage SDK could not be loaded', err);
    }
  }

  checkLocal(key: string): any {
    let storedData;
    const localStoragedata = localStorage.getItem(key) || '';
    if (this.isJsonString(localStoragedata)) storedData = JSON.parse(localStoragedata);
    else storedData = localStoragedata;
    if (!storedData) return null;

    const currentTime = new Date().getTime();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;

    if (currentTime - storedData.timestamp > thirtyDays) {
      localStorage.removeItem(key); // Remove outdated data
      return false;
    }
    return true;
  }

  commonDeviceDetails(cookieExistsPayzap: any, cookieExistsMycards: any) {
    const { browser, isDesktop, isMobile, isTablet } = getDeviceInfo();
    let deviceType = '';
    let platform_source = '';
    if (cookieExistsPayzap === true) {
      platform_source = 'PayZapp';
    } else if (cookieExistsMycards === true) {
      platform_source = 'MyCards';
    } else {
      platform_source = 'SmartBuy';
    }

    if (isMobile) {
      deviceType = 'mlite';
    } else if (isTablet) {
      deviceType = 'tablet';
    } else if (isDesktop) {
      deviceType = 'web';
    } else {
      deviceType = 'unknown';
    }
    this.basicValues = {
      ...this.basicValues,
      browser: browser,
      device_type: deviceType,
      program_name: this.programConfig?.program || 'SMARTBUY',
      sub_program: this.programConfig?.subProgram || '',
      platform_source: platform_source
    };
  }

  isJsonString(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  getUserType(custInfo?: any) {
    if (this.customerInfo && this.customerInfo['mobile']) {
      const userType = 'Registered User';
      const uniqueId = this.customerInfo['hashed_mobile'];
      this.moengage.add_mobile(uniqueId);
      return { userType, uniqueId };
    } else if (custInfo && custInfo['hashed_mobile']) {
      const userType = 'unregistered';
      const uniqueId = custInfo['hashed_mobile'];
      this.moengage.add_mobile(uniqueId);
      return { userType, uniqueId };
    } else {
      return { userType: 'unregistered' };
    }
  }

  checkMoeData() {
    const moeData = localStorage.getItem('MOE_DATA');
    if (moeData) {
      try {
        const parsedData = JSON.parse(moeData);
        if (parsedData && parsedData.USER_DATA && parsedData.USER_DATA.attributes) {
          const uniqueId = parsedData.USER_DATA.attributes.find(attr => attr.key === 'USER_ATTRIBUTE_UNIQUE_ID')?.value;
          return uniqueId || null;
        }
      } catch (e) {
        console.error('Error parsing MOE_DATA:', e);
      }
    }
    return null;
  }

  async trackAuthEvent(event = '', attr: any = {}) {
    try {
      if (this.moengageConfig?.enabled) {
        const moeUnqId = this.checkMoeData();
        const { uniqueId } = this.getUserType(attr);
        if (moeUnqId && moeUnqId !== uniqueId) {
          this.moengage.destroy_session().then(() => {
            this.moengage.add_unique_user_id(uniqueId).then(() => {
              this.moengage.add_user_attribute('language', 'en');
            });
          });
          this.moengage.add_mobile(uniqueId);
        } else if (!moeUnqId) {
          this.moengage.add_unique_user_id(uniqueId).then(() => {
            this.moengage.add_mobile(uniqueId);
          });
        }
        localStorage.setItem('moe_force_identify', uniqueId);
        attr = {
          ...attr,
          user_type: this.userType
        };
        await this.trackMoengageEvent(event, attr);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('trackAuthEvent: Error tracking MoEngage event:', error);
      return false;
    }
  }

  async destroySession() {
    try {
      if (!this.moengageConfig?.enabled) {
        return false;
      }
      await this.moengage.destroy_session();
      console.log('MoEngage session destroyed');
      return true;
    } catch (error) {
      console.error('Error destroying MoEngage session:', error);
      return false;
    }
  }

  async trackEvent(event = '', attr: any = {}): Promise<boolean> {
    if (!this.moengageConfig?.enabled) {
      return Promise.resolve(true);
    }
    try {
      const { userType } = this.getUserType();
      attr = { ...attr, user_type: userType };
      const res = await this.trackMoengageEvent(event, attr);
      return res;
    } catch (err) {
      console.error('trackEvent: Error tracking MoEngage event:', err);
      return false;
    }
  }

  async trackMoengageEvent(event: string, attr: any = {}): Promise<boolean> {
    if (!this.moengageConfig?.enabled) {
      return Promise.resolve(true);
    }
    attr = {
      ...this.basicValues,
      ...attr,
      referrer_url: document.referrer
    };

    try {
      await this.moengage?.track_event(event, attr);
      return true;
    } catch (error) {
      console.log('trackMoengageEvent', error);
      return false;
    }
  }
}

export const MoengageInstance = MoengageService.getInstance();
